import type { Place, PlaceStatus } from "@couples/shared";

import { supabase } from "./supabase";

interface PlaceRow {
  id: string;
  couple_id: string;
  created_by: string;
  name: string;
  memo: string | null;
  status: PlaceStatus;
  visited_on: string | null;
  created_at: string;
}

const COLUMNS = "id, couple_id, created_by, name, memo, status, visited_on, created_at";

function toPlace(row: PlaceRow): Place {
  return {
    id: row.id,
    coupleId: row.couple_id,
    createdBy: row.created_by,
    name: row.name,
    memo: row.memo,
    status: row.status,
    visitedOn: row.visited_on,
    createdAt: row.created_at,
  };
}

/** 行きたい/行った場所の一覧。絞り込みは画面側(status ごとに出し分ける) */
export async function fetchPlaces(): Promise<Place[]> {
  const { data, error } = await supabase
    .from("places")
    .select(COLUMNS)
    .order("created_at", { ascending: false });
  if (error) throw error;
  const rows: PlaceRow[] = data;
  return rows.map(toPlace);
}

export interface PlaceInput {
  name: string;
  memo: string | null;
  status: PlaceStatus;
  /** `YYYY-MM-DD`。status が visited のときだけ渡す */
  visitedOn: string | null;
}

/** couple_id / created_by は DB 側の既定値でセッションから埋まる */
export async function createPlace(input: PlaceInput): Promise<Place> {
  const { data, error } = await supabase
    .from("places")
    .insert({
      name: input.name,
      memo: input.memo,
      status: input.status,
      visited_on: input.visitedOn,
    })
    .select(COLUMNS)
    .single();
  if (error) throw error;
  return toPlace(data);
}

export async function updatePlace(id: string, input: PlaceInput): Promise<void> {
  const { error } = await supabase
    .from("places")
    .update({
      name: input.name,
      memo: input.memo,
      status: input.status,
      visited_on: input.visitedOn,
    })
    .eq("id", id);
  if (error) throw error;
}

export async function deletePlace(id: string): Promise<void> {
  const { error } = await supabase.from("places").delete().eq("id", id);
  if (error) throw error;
}
